import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
  useWindowDimensions,
  Modal,
  Pressable,
  Platform,
} from 'react-native';
import { useNavigation, CommonActions } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle as SvgCircle, Circle } from 'react-native-svg';
import {
  useStrategyOverviewQuery,
  useStrategyComparisonQuery,
  useStrategyOrgListQuery,
  useStrategyGoalsListQuery,
  useStrategyInitiativesListQuery,
  useStrategyKpisListQuery,
  useDueReadingsQuery,
  useEntityCountsQuery,
} from '../../hooks/queries';
import { OrganizationList, OrganizationComparison } from '../../types';
import { COLORS, FONTS } from '../../constants/config';
import { LoadingScreen, PageContainer, Navbar, Breadcrumb, OrganizationsComparisonSection } from '../../components';
import {
  StatCardSkeleton,
  GaugeCardSkeleton,
  DueReadingsSkeleton,
  DonutCardSkeleton,
  ComparisonChartSkeleton,
} from '../../components/Skeleton';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useResponsive, useRoleNavigation } from '../../hooks';
import { useAuth } from '../../contexts/AuthContext';
import { isEmployee, isOrgExecutive, isDG, canCreateStrategicGoals, isEvaluator } from '../../utils/userPermissions';
import { formatPct } from '../../utils/formatPercentage';
import { commitmentColor, deviationColor } from '../../utils/metricColors';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useYear } from '../../contexts/YearContext';
import { USER_TYPE_CONFIG, UserType } from '../../components/comments/types';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';

type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const BuildingIcon = ({ size = 20, color = '#A9211F' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 21H21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M5 21V7L13 3V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M19 21V11L13 7" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TargetIcon = ({ size = 20, color = '#A9211F' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FlagIcon = ({ size = 20, color = '#A9211F' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 15S5 14 8 14C11 14 13 16 16 16C19 16 20 15 20 15V3C20 3 19 4 16 4C13 4 11 2 8 2C5 2 4 3 4 3V15Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M4 22V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const UsersIcon = ({ size = 20, color = '#A9211F' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChartIcon = ({ size = 20, color = '#A9211F' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 20V10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 20V4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6 20V14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const TrendUpIcon = ({ size = 16, color = '#22C55E' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M23 6L13.5 15.5L8.5 10.5L1 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 6H23V12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronLeftIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronRightIcon = ({ size = 20, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ClockIcon = ({ size = 20, color = '#F59E0B' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 6V12L16 14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CheckCircleIcon = ({ size = 20, color = '#22C55E' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M22 4L12 14.01L9 11.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Target/Crosshair icon for goals
const CrosshairIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M22 12H18" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6 12H2" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 6V2" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 22V18" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Pointer/Flag icon for actual value
const PointerIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M3 11L22 2L13 21L11 13L3 11Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Flag icon for actual value (matching DashboardScreen)
const FlagIndicatorIcon = ({ size = 20, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M7 22V2L17 7L7 12" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Target/Eye icon for target value (matching DashboardScreen)
const TargetEyeIcon = ({ size = 24, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Government Building Icon (from assets/Icon-svg/building.svg)
const GovBuildingIcon = ({ size = 24, color = '#22C55E' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V4C20 2.89543 19.1046 2 18 2Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 22V18H15V22" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 6H8.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 6H16.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 6H12.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 10H12.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 14H12.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 10H16.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 14H16.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 10H8.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 14H8.01" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Strategic Goals Icon (from assets/Icon-svg/target.svg)
const StrategicGoalIcon = ({ size = 24, color = '#22C55E' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 18C15.3137 18 18 15.3137 18 12C18 8.68629 15.3137 6 12 6C8.68629 6 6 8.68629 6 12C6 15.3137 8.68629 18 12 18Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 14C13.1046 14 14 13.1046 14 12C14 10.8954 13.1046 10 12 10C10.8954 10 10 10.8954 10 12C10 13.1046 10.8954 14 12 14Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// KPI/Chart bars Icon (from assets/Icon-svg/chart-no-axes-column.svg)
const KPIBarsIcon = ({ size = 24, color = '#22C55E' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 20V10" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 20V4" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6 20V14" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Rocket Icon for initiatives (from assets/Icon-svg/rocket.svg)
const RocketIcon = ({ size = 24, color = '#22C55E' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4.5 16.4993C3 17.7593 2.5 21.4993 2.5 21.4993C2.5 21.4993 6.24 20.9993 7.5 19.4993C8.21 18.6593 8.2 17.3693 7.41 16.5893C7.02131 16.2184 6.50929 16.004 5.97223 15.9874C5.43516 15.9708 4.91088 16.1531 4.5 16.4993Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15.0002L9 12.0002C9.53214 10.6197 10.2022 9.29631 11 8.05025C12.1652 6.18723 13.7876 4.6533 15.713 3.59434C17.6384 2.53538 19.8027 1.98662 22 2.00025C22 4.72025 21.22 9.50025 16 13.0002C14.7369 13.799 13.3968 14.469 12 15.0002Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 11.9995H4C4 11.9995 4.55 8.96953 6 7.99953C7.62 6.91953 11 7.99953 11 7.99953" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15V20C12 20 15.03 19.45 16 18C17.08 16.38 16 13 16 13" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Small Check Icon for positive status
const SmallCheckIcon = ({ size = 14, color = '#22C55E' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M20 6L9 17L4 12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Small X/Warning Icon for pending status
const SmallWarningIcon = ({ size = 14, color = '#F59E0B' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M18 6L6 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6 6L18 18" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Circle Percent Icon (from assets/Icon-svg/circle-percent.svg)
const CirclePercentIcon = ({ size = 24, color = '#22C55E' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 10C9.55228 10 10 9.55228 10 9C10 8.44772 9.55228 8 9 8C8.44772 8 8 8.44772 8 9C8 9.55228 8.44772 10 9 10Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M15 16C15.5523 16 16 15.5523 16 15C16 14.4477 15.5523 14 15 14C14.4477 14 14 14.4477 14 15C14 15.5523 14.4477 16 15 16Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M15 9L9 15" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Clock Icon (from assets/Icon-svg/clock.svg)
const ClockStatIcon = ({ size = 24, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 6V12L16 14" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Eye Icon (from assets/Icon-svg/eye.svg)
const EyeIcon = ({ size = 14, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M2.06153 12.3474C1.97819 12.1229 1.97819 11.8759 2.06153 11.6514C2.87323 9.68324 4.25104 8.00042 6.0203 6.81628C7.78955 5.63214 9.87057 5 11.9995 5C14.1285 5 16.2095 5.63214 17.9788 6.81628C19.748 8.00042 21.1258 9.68324 21.9375 11.6514C22.0209 11.8759 22.0209 12.1229 21.9375 12.3474C21.1258 14.3155 19.748 15.9983 17.9788 17.1825C16.2095 18.3666 14.1285 18.9988 11.9995 18.9988C9.87057 18.9988 7.78955 18.3666 6.0203 17.1825C4.25104 15.9983 2.87323 14.3155 2.06153 12.3474Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Trending Up Icon (from assets/Icon-svg/trending-up.svg)
const TrendingUpIcon = ({ size = 14, color = '#22C55E' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M16 7H22V13" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M22 7L13.5 15.5L8.5 10.5L2 17" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ---- Figma stat-card icons (nodes 6322:172163 & 6322:172168) ----

// triangle-alert — Strategic Risks
const TriangleAlertIcon = ({ size = 24, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 9v4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 17h.01" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// goal — Strategic Goals (filled target with arrow pointer)
const GoalTargetIcon = ({ size = 24, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 13V2l4 4-4 4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M20 10a10 10 0 1 1-7-9.57" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 10a5 5 0 1 1-5-5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// building-2 — Government Entities
const Building2Icon = ({ size = 24, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M10 6h4M10 10h4M10 14h4M10 18h4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// rocket — Strategic Initiatives (for Figma stat card)
const RocketStatIcon = ({ size = 24, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15 9 12a15.43 15.43 0 0 1 2-5.05A14.8 14.8 0 0 1 22 2c0 2.72-.78 7.5-6 11-1.26.8-2.6 1.47-4 2Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// users — Human Capital
const UsersStatIcon = ({ size = 24, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 3.13a4 4 0 0 1 0 7.75" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// boxes — Organizational Units
const BoxesIcon = ({ size = 24, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M2.97 12.92 2 12.5l.97-.42a1 1 0 0 0 .53-.89V6.5a1 1 0 0 1 .53-.89L7 4l4 2V9" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="m7 16.5-4-2.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M7 16.5v-5l-4-2.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="m21 12.5.97.42a1 1 0 0 1 .53.89v3.69a1 1 0 0 1-.53.89L17 21l-5-2.5V14" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="m17 21-5-2.5V14l5 2.5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 16.5v-5l-5-2.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="m12 9 5-2.5L12 4 7 6.5 12 9Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// hand-services — Services
const HandServicesIcon = ({ size = 24, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 12h3a2 2 0 0 0 2-2 2 2 0 0 0-2-2H9.5a3 3 0 0 0-2.12.88L3 13.25" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="m7 21 3.37-3.37A3 3 0 0 1 12.5 17H18a3 3 0 0 0 2.56-1.44l2-3.13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 17l5 5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 4.5 20 8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M13 7.5 17 4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// file-cog — Main Processes
const FileCogIcon = ({ size = 24, color = '#414042' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2v6h6" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 17a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 11.5v-1M12 18.5v-1M9.75 12.75l.5.5M14.25 15.75l-.5-.5M10.5 14.5h-1M14.5 14.5h-1M10.25 15.75l-.5.5M13.75 12.75l.5-.5" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Figma Stat Card (nodes 6322:172163 / 172168 — ArCard4)
// Title (right) + Icon (left) row, big centered value, optional green chip
// ============================================================
type FigmaStatCardProps = {
  title: string;
  value: number;
  icon: React.ReactNode;
  chip?: string | null;
  onPress?: () => void;
  themedCardStyle: { backgroundColor: string; borderColor: string };
  valueColor: string;
  titleColor: string;
};

const FigmaStatCard = ({
  title, value, icon, chip, onPress,
  themedCardStyle, valueColor, titleColor,
}: FigmaStatCardProps) => {
  const content = (
    <>
      <View style={styles.figmaStatCardHeader}>
        <Text style={[styles.figmaStatCardTitle, { color: titleColor }]}>{title}</Text>
        <View style={styles.figmaStatCardIcon}>{icon}</View>
      </View>
      <View style={styles.figmaStatCardBody}>
        <Text style={[styles.figmaStatCardValue, { color: valueColor }]}>{value}</Text>
        {chip ? (
          <View style={styles.figmaStatCardChip}>
            <Text style={styles.figmaStatCardChipText}>{chip}</Text>
          </View>
        ) : null}
      </View>
    </>
  );

  if (onPress) {
    return (
      <TouchableOpacity
        style={[styles.figmaStatCard, themedCardStyle]}
        onPress={onPress}
        activeOpacity={0.7}
      >
        {content}
      </TouchableOpacity>
    );
  }
  return <View style={[styles.figmaStatCard, themedCardStyle]}>{content}</View>;
};

// ============================================================
// Progress Circle Component (Donut Chart)
// ============================================================

interface ProgressCircleProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
  showLabel?: boolean;
  labelSize?: number;
  textColor?: string;
}

const ProgressCircle = ({
  percentage,
  size = 120,
  strokeWidth = 10,
  color = '#22C55E',
  backgroundColor,
  showLabel = true,
  labelSize = 24,
  textColor = '#303030',
}: ProgressCircleProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const safePercentage = Math.min(Math.max(percentage, 0), 100);
  const strokeDashoffset = circumference - (safePercentage / 100) * circumference;
  // Background ring defaults to a translucent (~20%) version of the
  // progress color so it matches the SegmentedGauge theme.
  const ringBgColor = backgroundColor ?? `${color}33`;

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size} style={{ transform: [{ rotate: '-90deg' }] }}>
        <SvgCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={ringBgColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <SvgCircle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={strokeDashoffset}
        />
      </Svg>
      {showLabel && (
        <Text style={[styles.progressLabel, { fontSize: labelSize, color: textColor }]}>
          %{formatPct(safePercentage)}
        </Text>
      )}
    </View>
  );
};

// ============================================================
// Circular Gauge Component (matching DashboardScreen)
// ============================================================

interface CircularGaugeProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
}

const CircularGauge = ({
  percentage,
  size = 120,
  strokeWidth = 12,
  color = '#2CA02C',
  backgroundColor,
}: CircularGaugeProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;
  // Default the background ring to a translucent (~20%) version of the
  // foreground color so it matches the SegmentedGauge theme.
  const ringBgColor = backgroundColor ?? `${color}33`;

  // Calculate dot position at END of progress arc
  // -90 offset to start from top (12 o'clock position)
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle - translucent same color as progress arc */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={ringBgColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress arc - rotation applied to this element only */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={color}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot at end of progress - ONLY show if percentage > 0 */}
        {percentage > 0 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={strokeWidth / 2 + 2}
            fill={color}
            stroke="#FCFCFC"
            strokeWidth={2}
          />
        )}
      </Svg>
      {/* Percentage text */}
      <View style={{ position: 'absolute', alignItems: 'center', justifyContent: 'center', width: size, height: size }}>
        <Text
          numberOfLines={1}
          adjustsFontSizeToFit
          style={{ fontSize: Math.round(size / 5), fontFamily: FONTS.numbers, fontWeight: '500', color: '#414042' }}
        >
          % {formatPct(percentage)}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Segmented Gauge Component (matching Figma مؤشر تحقيق الرؤية)
// ============================================================

interface SegmentedGaugeProps {
  actual: number;
  target: number;
  size?: number;
  showLabel?: boolean;
  strokeWidth?: number;
  showDot?: boolean;
  color?: string;
}

const SegmentedGauge = ({ actual, target, size = 120, showLabel = true, strokeWidth = 8, showDot = true, color = '#2CA02C' }: SegmentedGaugeProps) => {
  const ringBgColor = `${color}33`; // ~20% opacity
  const wedgeFillColor = `${color}73`; // ~45% opacity
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = radius * 2 * Math.PI;

  const actualPercentage = Math.min(Math.max(actual, 0), 100);
  const targetPercentage = Math.min(Math.max(target, 0), 100);
  const actualOffset = circumference - (actualPercentage / 100) * circumference;

  // Dot position at end of ACTUAL arc (progress indicator)
  const dotAngleRad = -Math.PI / 2 + (actualPercentage / 100) * 2 * Math.PI;
  const dotX = center + radius * Math.cos(dotAngleRad);
  const dotY = center + radius * Math.sin(dotAngleRad);
  const dotRadius = Math.max(3, Math.round(strokeWidth * 0.6));

  // Pie wedge path showing TARGET (filled backdrop area inside the ring)
  const getTargetWedgePath = () => {
    if (targetPercentage <= 0) return '';
    const startAngleRad = -Math.PI / 2; // 12 o'clock
    const endAngleRad = startAngleRad + (targetPercentage / 100) * 2 * Math.PI;
    const innerR = radius - strokeWidth / 2;
    const x1 = center + innerR * Math.cos(startAngleRad);
    const y1 = center + innerR * Math.sin(startAngleRad);
    const x2 = center + innerR * Math.cos(endAngleRad);
    const y2 = center + innerR * Math.sin(endAngleRad);
    const largeArc = targetPercentage > 50 ? 1 : 0;
    return `M ${center} ${center} L ${x1} ${y1} A ${innerR} ${innerR} 0 ${largeArc} 1 ${x2} ${y2} Z`;
  };

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background ring */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={ringBgColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Inner pie wedge - TARGET filled backdrop */}
        {targetPercentage > 0 && (
          <Path
            d={getTargetWedgePath()}
            fill={wedgeFillColor}
          />
        )}
        {/* Outer ring arc - ACTUAL progress indicator (solid, flat ends) */}
        {actualPercentage > 0 && (
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={actualOffset}
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
        {/* Dot at end of ACTUAL arc (only when showDot enabled) */}
        {showDot && actualPercentage > 0 && actualPercentage < 100 && (
          <Circle
            cx={dotX}
            cy={dotY}
            r={dotRadius}
            fill={color}
            stroke="#FCFCFC"
            strokeWidth={1.5}
          />
        )}
      </Svg>
      {/* Percentage text */}
      {showLabel && (
        <View style={{ position: 'absolute', alignItems: 'center', justifyContent: 'center', width: size, height: size }}>
          <Text
            numberOfLines={1}
            adjustsFontSizeToFit
            style={{ fontSize: Math.round(size / 5), fontFamily: FONTS.numbers, fontWeight: '500', color: '#414042' }}
          >
            % {formatPct(actual)}
          </Text>
        </View>
      )}
    </View>
  );
};

// ============================================================
// Initiative Status Donut Chart Component (matching Figma)
// ============================================================

interface DonutChartData {
  planning: number;
  inProgress: number;
  completed: number;
  total: number;
}

const InitiativeDonutChart = ({ data }: { data: DonutChartData }) => {
  const size = 160;
  const strokeWidth = 32;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = radius * 2 * Math.PI;

  // Calculate percentages
  const total = data.total || 1; // Avoid division by zero
  const planningPct = (data.planning / total) * 100;
  const inProgressPct = (data.inProgress / total) * 100;
  const completedPct = (data.completed / total) * 100;

  // Calculate stroke dash arrays for each segment
  const planningDash = (planningPct / 100) * circumference;
  const inProgressDash = (inProgressPct / 100) * circumference;
  const completedDash = (completedPct / 100) * circumference;

  // Calculate rotation offsets for each segment
  const planningOffset = 0;
  const inProgressOffset = planningDash;
  const completedOffset = planningDash + inProgressDash;

  // Colors from Figma
  const COLORS = {
    planning: '#1F77B4',    // Blue - تحت التخطيط
    inProgress: '#C67EFF',  // Purple - قيد التنفيذ
    completed: '#2CA02C',   // Green - مكتملة
  };

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Planning segment (Blue) */}
        {planningPct > 0 && (
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke={COLORS.planning}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${planningDash} ${circumference - planningDash}`}
            strokeLinecap="butt"
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
        {/* In Progress segment (Purple) */}
        {inProgressPct > 0 && (
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke={COLORS.inProgress}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${inProgressDash} ${circumference - inProgressDash}`}
            strokeDashoffset={-inProgressOffset}
            strokeLinecap="butt"
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
        {/* Completed segment (Green) */}
        {completedPct > 0 && (
          <Circle
            cx={center}
            cy={center}
            r={radius}
            stroke={COLORS.completed}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${completedDash} ${circumference - completedDash}`}
            strokeDashoffset={-completedOffset}
            strokeLinecap="butt"
            transform={`rotate(-90 ${center} ${center})`}
          />
        )}
      </Svg>
    </View>
  );
};

// ============================================================
// Vertical Bar Chart Component (matching Figma design)
// ============================================================

interface VerticalBarData {
  name: string;
  value: number; // Performance percentage
}

// Calendar Icon for date pickers
const CalendarIcon = ({ size = 18, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M16 2V6" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 2V6" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M3 10H21" stroke={color} strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// Dropdown/Chevron Icon
const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const VerticalBarChart = ({ data }: { data: VerticalBarData[] }) => {
  const maxValue = 100;
  const chartHeight = 240; // Matching Figma exactly

  // Y-axis labels (from top to bottom: 100% -> 0%)
  const yAxisLabels = ['100%', '90%', '80%', '70%', '60%', '50%', '40%', '30%', '20%', '10%', '0%'];

  // Bar color uses the unified commitment scheme so the strategy bar
  // chart matches the donut/gauge thresholds across the page:
  //   >= 80 → green, 50–79 → orange, < 50 → red.
  const getBarColor = (percentage: number): string => commitmentColor(percentage);

  // Calculate dynamic bar width based on data count
  const getBarWidth = (dataLength: number): number => {
    if (dataLength === 1) return 80;      // Single bar: 80px wide
    if (dataLength <= 3) return 70;       // Few bars: 70px wide
    if (dataLength <= 6) return 60;       // Medium: 60px wide
    return 48;                             // Many bars: default width
  };

  // Calculate column width based on data count.
  // Wider columns let long Arabic department names wrap onto multiple
  // lines instead of being truncated with an ellipsis.
  const getColumnWidth = (dataLength: number): number => {
    if (dataLength === 1) return 140;     // Single bar: wider column
    if (dataLength <= 3) return 120;      // Few bars
    if (dataLength <= 6) return 110;      // Medium
    if (dataLength <= 10) return 100;     // Many bars
    return 95;                             // Very many bars
  };

  const barWidth = getBarWidth(data.length);
  const columnWidth = getColumnWidth(data.length);

  // Determine if bars should be centered (when few items)
  const shouldCenterBars = data.length <= 3;

  return (
    <View style={styles.verticalChartContainer}>
      {/* Y-Axis on LEFT */}
      <View style={styles.yAxis}>
        {yAxisLabels.map((label, index) => (
          <Text key={index} style={styles.yAxisLabel}>{label}</Text>
        ))}
      </View>

      {/* Chart Area - Always full width */}
      <View style={styles.chartAreaWrapper}>
        <View style={styles.chartAreaFullWidth}>
          {/* Grid Lines */}
          <View style={styles.gridLines}>
            {yAxisLabels.map((_, index) => (
              <View key={index} style={styles.gridLine} />
            ))}
          </View>

          {/* Bars Container - All bars share same baseline */}
          <View style={[
            styles.barsSection,
            shouldCenterBars && styles.barsSectionCentered
          ]}>
            {/* Bars Row - Fixed height, bars grow UP from bottom */}
            <View style={[
              styles.barsRow,
              shouldCenterBars && styles.barsRowCentered
            ]}>
              {data.map((item, index) => {
                const barHeight = Math.max((item.value / maxValue) * chartHeight, 4);
                return (
                  <View key={index} style={[styles.verticalBarColumn, { width: columnWidth }]}>
                    {/* Bar grows from bottom (baseline at 0%) */}
                    <View
                      style={[
                        styles.verticalBar,
                        {
                          height: barHeight,
                          backgroundColor: getBarColor(item.value),
                          width: barWidth,
                        },
                      ]}
                    />
                  </View>
                );
              })}
            </View>
            {/* Labels Row - Separate from bars */}
            <View style={[
              styles.labelsRow,
              shouldCenterBars && styles.labelsRowCentered
            ]}>
              {data.map((item, index) => (
                <View key={index} style={[styles.labelColumn, { width: columnWidth }]}>
                  <Text style={styles.verticalBarLabel}>{item.name}</Text>
                </View>
              ))}
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

// ============================================================
// Dashboard Stats Interface
// ============================================================

interface DashboardStats {
  organizations_count: number;
  goals_count: number;
  initiatives_count: number;
  kpis_count: number;
  users_count: number;
  // Vision achievement metrics (matching Figma design)
  commitment_index: number; // مؤشر الالتزام بتحقيق الرؤية (large card)
  vision_achievement: number; // مؤشر تحقيق الرؤية (small card donut)
  target_percentage: number; // الهدف (النسبة المطلوبة)
  actual_percentage: number; // القيمة الفعلية المحققة
  // Other stats
  delayed_projects_percentage: number;
  completed_initiatives_percentage: number;
  // Stats row counts (matching Figma)
  pending_goals_count: number; // أهداف بحاجة اعتماد
  pending_initiatives_count: number; // مبادرات بحاجة اعتماد
  excellent_kpis_count: number; // مؤشرات ممتازة
  all_orgs_active: boolean; // جميع الجهات مفعلة
  // Second stats row (matching Figma node 2585-94264)
  initiative_kpis_count: number; // مؤشرات المبادرات الاستراتيجية
  initiative_excellent_kpis: number; // مؤشرات المبادرات الممتازة
  process_kpis_count: number; // مؤشرات العمليات الرئيسية
  process_excellent_kpis: number; // مؤشرات العمليات الممتازة
  overall_achievement_rate: number; // معدل الإنجاز العام
  achievement_improvement: number; // تحسن عن الشهر الماضي
  performance_deviation: number | null; // نسبة انحرافات الأداء
  // Figma stat-card rows (6322:172163 + 6322:172168)
  risks_count: number;           // المخاطر الاستراتيجية
  units_count: number;           // الوحدات التنظيمية
  services_count: number;        // الخدمات
  processes_count: number;       // العمليات الرئيسية
  sub_processes_count: number;   // مؤشر العمليات الفرعية
  employees_count: number;       // رأس المال البشري
  unit_goal_kpis_count: number;  // مؤشر وحدة تنظيمية
}

interface OrganizationWithStats extends OrganizationList {
  vision_achievement?: number;
  commitment_index?: number;
  goals_count?: number;
  improvement?: number;
  pending_goals_count?: number;
  pending_initiatives_count?: number;
}

// ============================================================
// Main Component
// ============================================================

export default function StrategyDashboardScreen() {
  const navigation = useNavigation<NavigationProp>();
  const { width } = useWindowDimensions();
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding } = useResponsive();
  const { canSeePending, isViewOnly } = useRoleNavigation();
  const { user } = useAuth();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const rtlRow = isRTL ? 'row-reverse' as const : 'row' as const;
  const { selectedYear } = useYear();
  const { translate, translateOrg } = useAutoTranslate();
  const isWideScreen = width >= 1024;

  // Dynamic theme styles for cards
  const themedCardStyles = {
    card: {
      backgroundColor: colors.card,
      borderColor: colors.cardBorder,
    },
    headerTitle: {
      color: colors.primary,
    },
    headerSubtitle: {
      color: colors.textMuted,
    },
    cardTitle: {
      color: colors.textPrimary,
    },
    cardValue: {
      color: colors.text,
    },
    cardDescription: {
      color: colors.textMuted,
    },
    sectionTitle: {
      color: colors.textPrimary,
    },
    tableHeader: {
      backgroundColor: isDark ? colors.gray100 : '#F9FAFB',
    },
    tableRow: {
      borderBottomColor: colors.divider,
    },
    tableCell: {
      color: colors.text,
    },
    progressText: colors.textPrimary,
    circleBg: isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(51, 65, 85, 0.2)',
  };

  // Get user role label for header
  const userType = user?.profile?.user_type as UserType | undefined;
  const userRoleLabel = userType ? USER_TYPE_CONFIG[userType]?.label : t('common.user');

  // 2026-05-14: the executive-style strategy dashboard is hidden for
  // every role per user direction. Anyone who reaches this screen
  // (typically via a stale /strategy URL) is now bounced to the
  // unified DashboardStack — same surface every other role uses.
  useEffect(() => {
    if (!user) return;
    navigation.dispatch(
      CommonActions.reset({
        index: 0,
        routes: [{ name: 'DashboardStack' as any }],
      })
    );
  }, [user, navigation]);

  // ---------------- React Query data sources ----------------
  // The dashboard was previously fetched by a 300-line `fetchData` callback
  // that set 5+ useState slots. Everything below is derived — raw data comes
  // from the 5 parallel list calls bundled in `useStrategyDashboardQuery`.
  // Per-pillar queries — each /strategy section fetches only what it needs
  // and renders its own skeleton until ready. The legacy
  // `useStrategyDashboardQuery` bundled these into one blocking call; we still
  // synthesize a `dashboardData` object below so the rest of this screen's
  // `dashboardData?.X` references keep working unchanged.
  const orgListQ = useStrategyOrgListQuery(selectedYear);
  const goalsListQ = useStrategyGoalsListQuery(selectedYear);
  const initListQ = useStrategyInitiativesListQuery(selectedYear);
  const kpisListQ = useStrategyKpisListQuery(selectedYear);
  const schedulesQ = useDueReadingsQuery(selectedYear);
  const countsQ = useEntityCountsQuery(selectedYear);

  const dashboardData = useMemo(() => ({
    organizations: orgListQ.data?.results ?? [],
    organizationsCount: orgListQ.data?.count ?? 0,
    goals: goalsListQ.data?.results ?? [],
    goalsCount: goalsListQ.data?.count ?? 0,
    initiatives: initListQ.data?.results ?? [],
    initiativesCount: initListQ.data?.count ?? 0,
    kpis: kpisListQ.data?.results ?? [],
    kpisCount: kpisListQ.data?.count ?? 0,
    schedules: schedulesQ.data ?? [],
    usersCount: countsQ.data?.usersCount ?? 0,
    risksCount: countsQ.data?.risksCount ?? 0,
    unitsCount: countsQ.data?.unitsCount ?? 0,
    servicesCount: countsQ.data?.servicesCount ?? 0,
    processesCount: countsQ.data?.processesCount ?? 0,
    subProcessesCount: countsQ.data?.subProcessesCount ?? 0,
    employeesCount: countsQ.data?.employeesCount ?? 0,
  }), [orgListQ.data, goalsListQ.data, initListQ.data, kpisListQ.data, schedulesQ.data, countsQ.data]);

  // Per-pillar pending flags drive the skeleton fallback for each section.
  // First-load only — `placeholderData: keepPreviousData` keeps content
  // visible during year-switch refetches.
  const pillarPending = {
    orgs: orgListQ.data === undefined,
    goals: goalsListQ.data === undefined,
    initiatives: initListQ.data === undefined,
    kpis: kpisListQ.data === undefined,
    schedules: schedulesQ.data === undefined,
    counts: countsQ.data === undefined,
  };

  // Composite "any pillar still loading" flag — used only by the pull-to-
  // refresh control. The page no longer short-circuits to a blocking spinner.
  const refreshing =
    orgListQ.isRefetching || goalsListQ.isRefetching || initListQ.isRefetching ||
    kpisListQ.isRefetching || schedulesQ.isRefetching || countsQ.isRefetching;

  const refetchDashboard = useCallback(() => {
    orgListQ.refetch();
    goalsListQ.refetch();
    initListQ.refetch();
    kpisListQ.refetch();
    schedulesQ.refetch();
    countsQ.refetch();
  }, [orgListQ, goalsListQ, initListQ, kpisListQ, schedulesQ, countsQ]);

  // Aggregated stats across ALL orgs — backend-computed so the dashboard
  // cards reflect the full 15-org portfolio (counts summed, vision via stage
  // engine, excellent KPIs via cumulative engine, simple-avg achievement &
  // deviation per user request).
  const {
    data: overviewData,
    refetch: refetchOverview,
  } = useStrategyOverviewQuery(selectedYear);

  // Chart filter states (UI-only — not fetched)
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null); // null = كل الجهات
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [fromDate, setFromDate] = useState<Date>(() => {
    const date = new Date();
    date.setMonth(0, 1); // January 1st of current year
    return date;
  });
  const [toDate, setToDate] = useState<Date>(new Date());
  const [showFromDatePicker, setShowFromDatePicker] = useState(false);
  const [showToDatePicker, setShowToDatePicker] = useState(false);

  // Comparison chart: sort state is a UI concern, but the actual fetch is
  // keyed on it — each sort permutation gets its own React Query cache entry.
  const [comparisonSortBy, setComparisonSortBy] = useState<string>('vision_achievement');
  const [comparisonOrder, setComparisonOrder] = useState<'asc' | 'desc'>('desc');
  // Strategy Team / DG / superuser / evaluators see the org comparison section.
  // Evaluator detection now uses the sms.* permissions — Phase B (2026-05-13).
  const canSeeComparison = (
    canCreateStrategicGoals(user)
    || isDG(user)
    || !!user?.is_superuser
    || isEvaluator(user)
  );
  const {
    data: comparisonData = [],
    isFetching: comparisonLoading,
    refetch: refetchComparison,
  } = useStrategyComparisonQuery({
    sortBy: comparisonSortBy,
    order: comparisonOrder,
    enabled: canSeeComparison,
  });

  // Format date for display (DD-M-YYYY)
  const formatDate = (date: Date): string => {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Format date for HTML input (YYYY-MM-DD)
  const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Date-filter placeholder effect removed — it only logged to console and
  // never actually invoked a fetch. Reintroduce a real effect here if/when
  // the backend supports date-range filtering for the dashboard.
  //
  // `getFilteredChartData` now lives *below* the `organizations` useMemo
  // because it reads from it. See line ~1090 for the actual definition.

  // ---------------- Derived stats (memoized over raw query results) ----------------
  // Previously all of this lived inside an async `fetchData` callback that
  // ran on every navigation focus and called setState for each slot. Now the
  // raw data comes from React Query and these heavy reduce/filter passes
  // only re-run when the underlying data array identity actually changes.

  // Due readings — buckets schedules into today / this-week / this-month.
  // Honors the org-picker: when a specific department is selected, only
  // count schedules whose KPI belongs to that department (mixed-department
  // totals used to leak through because this memo ignored selectedOrgId).
  const dueReadings = useMemo(() => {
    const schedules = dashboardData?.schedules ?? [];
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    let todayCount = 0;
    let weekCount = 0;
    let monthCount = 0;
    schedules.forEach((schedule: any) => {
      if (schedule.status === 'completed' || schedule.is_completed) return;
      if (selectedOrgId !== null && schedule.department_id !== selectedOrgId) return;
      const dueDate = new Date(schedule.due_date || schedule.scheduled_date || schedule.period_end_date);
      if (isNaN(dueDate.getTime())) return;
      if (dueDate.toDateString() === today.toDateString()) todayCount++;
      if (dueDate >= weekStart && dueDate <= weekEnd) weekCount++;
      if (dueDate >= monthStart && dueDate <= monthEnd) monthCount++;
    });
    return { today: todayCount, thisWeek: weekCount, thisMonth: monthCount };
  }, [dashboardData?.schedules, selectedOrgId]);

  // Initiative status breakdown for the donut chart
  const initiativeStatus = useMemo(() => {
    const initiatives = dashboardData?.initiatives ?? [];
    return {
      planning: initiatives.filter((i: any) => i.status === 'planning').length,
      inProgress: initiatives.filter((i: any) => i.status === 'in_progress').length,
      completed: initiatives.filter((i: any) => i.status === 'completed').length,
      total: initiatives.length,
    };
  }, [dashboardData?.initiatives]);

  // Strategic goals date range — earliest start_date and latest target_completion_date
  // Used by the comparison chart's من تاريخ / إلى تاريخ defaults so they reflect
  // the actual strategic-goals planning window instead of the calendar year.
  const goalsDateRange = useMemo(() => {
    const goals = dashboardData?.goals ?? [];
    let from: string | null = null;
    let to: string | null = null;
    for (const g of goals) {
      if (g.start_date && (!from || g.start_date < from)) from = g.start_date;
      if (g.target_completion_date && (!to || g.target_completion_date > to)) to = g.target_completion_date;
    }
    return { fromDate: from, toDate: to };
  }, [dashboardData?.goals]);

  // Per-org vision + pending maps, then merged into OrganizationWithStats[]
  const organizations = useMemo<OrganizationWithStats[]>(() => {
    if (!dashboardData) return [];
    const goals = dashboardData.goals;
    const initiatives = dashboardData.initiatives;
    const orgs = dashboardData.organizations;

    const orgAchievementMap: Record<number, { total: number; count: number }> = {};
    const orgPendingGoalsMap: Record<number, number> = {};
    const orgPendingInitiativesMap: Record<number, number> = {};

    goals.forEach((goal: any) => {
      const orgId = goal.organization;
      if (!orgAchievementMap[orgId]) orgAchievementMap[orgId] = { total: 0, count: 0 };
      orgAchievementMap[orgId].total += (goal.achievement_percentage ?? goal.progress_percentage ?? 0);
      orgAchievementMap[orgId].count += 1;
      if (goal.status === 'pending') {
        orgPendingGoalsMap[orgId] = (orgPendingGoalsMap[orgId] ?? 0) + 1;
      }
    });
    initiatives.forEach((init: any) => {
      const orgId = init.organization ?? init.goal_organization;
      if (init.approval_status === 'pending') {
        orgPendingInitiativesMap[orgId] = (orgPendingInitiativesMap[orgId] ?? 0) + 1;
      }
    });

    return orgs.map((org: any) => {
      let visionAchievementValue = org.vision_achievement;
      if (visionAchievementValue === undefined || visionAchievementValue === null) {
        const achievementData = orgAchievementMap[org.id];
        visionAchievementValue = achievementData && achievementData.count > 0
          ? achievementData.total / achievementData.count
          : 0;
      }
      return {
        ...org,
        vision_achievement: visionAchievementValue,
        pending_goals_count: orgPendingGoalsMap[org.id] ?? 0,
        pending_initiatives_count: orgPendingInitiativesMap[org.id] ?? 0,
      } as OrganizationWithStats;
    });
  }, [dashboardData]);

  // Summary stats (used by every stat card on the dashboard)
  const stats = useMemo<DashboardStats | null>(() => {
    if (!dashboardData) return null;
    const orgs = dashboardData.organizations;
    const goals = dashboardData.goals;
    const initiatives = dashboardData.initiatives;
    const kpis = dashboardData.kpis;
    const now = new Date();

    // Vision + commitment from stage progress
    let visionAchievement = 0;
    let targetPercentage = 0;
    let actualPercentage = 0;
    if (orgs.length > 0) {
      const totalRequired = orgs.reduce(
        (sum: number, org: any) => sum + Number(org.stage_required_progress ?? 0), 0);
      const totalActual = orgs.reduce(
        (sum: number, org: any) => sum + Number(org.stage_actual_progress ?? 0), 0);
      const totalCapacity = orgs.reduce(
        (sum: number, org: any) => sum + Number(org.stage_capacity ?? 0), 0);
      targetPercentage = totalCapacity > 0 ? Number(((totalRequired / totalCapacity) * 100).toFixed(2)) : 0;
      actualPercentage = totalCapacity > 0 ? Number(((totalActual / totalCapacity) * 100).toFixed(2)) : 0;
      visionAchievement = actualPercentage;
    }
    const commitmentIndex = targetPercentage > 0
      ? Number(((actualPercentage / targetPercentage) * 100).toFixed(2))
      : 0;

    // Initiative percentages
    const completedInitiatives = initiatives.filter((i: any) => i.status === 'completed').length;
    const completedPercentage = initiatives.length > 0
      ? (completedInitiatives / initiatives.length) * 100 : 0;
    const delayedInitiatives = initiatives.filter(
      (i: any) => i.status !== 'completed' && new Date(i.end_date) < now,
    ).length;
    const delayedPercentage = initiatives.length > 0
      ? (delayedInitiatives / initiatives.length) * 100 : 0;

    // Pending counts
    const pendingGoalsCount = goals.filter((g: any) => g.status === 'pending').length;
    const pendingInitiativesCount = initiatives.filter((i: any) => i.approval_status === 'pending').length;

    // KPI buckets by source
    const strategicGoalKpis = kpis.filter((k: any) => k.source_type === 'strategic_goal');
    const excellentKpisCount = strategicGoalKpis.filter(
      (k: any) => (k.achievement_percentage ?? 0) >= 100 || k.status === 'excellent',
    ).length;
    const initiativeKpis = kpis.filter((k: any) => k.source_type === 'initiative');
    const initiativeExcellentKpis = initiativeKpis.filter(
      (k: any) => (k.achievement_percentage ?? 0) >= 100,
    ).length;
    const processKpis = kpis.filter(
      (k: any) => k.source_type === 'process' || k.source_type === 'sub_process',
    );
    const processExcellentKpis = processKpis.filter(
      (k: any) => (k.achievement_percentage ?? 0) >= 100,
    ).length;
    const unitGoalKpisCount = kpis.filter(
      (k: any) => k.source_type === 'unit_goal' || k.source_type === 'organizational_unit',
    ).length;

    // Overall achievement rate (average of all KPIs with a recorded achievement)
    let overallAchievementRate = 0;
    if (kpis.length > 0) {
      const withAchievement = kpis.filter((k: any) => k.achievement_percentage !== undefined);
      if (withAchievement.length > 0) {
        overallAchievementRate = withAchievement.reduce(
          (sum: number, k: any) => sum + (k.achievement_percentage ?? 0), 0,
        ) / withAchievement.length;
      }
    }

    // Performance deviation (% of KPIs below target)
    const kpisWithTarget = kpis.filter(
      (k: any) => k.target_value !== undefined && k.current_value !== undefined,
    );
    let performanceDeviation: number | null = null;
    if (kpisWithTarget.length > 0) {
      const deviating = kpisWithTarget.filter(
        (k: any) => (k.achievement_percentage ?? 0) < 100,
      ).length;
      performanceDeviation = (deviating / kpisWithTarget.length) * 100;
    }

    // Client-side computed fallbacks (used only when the aggregated backend
    // overview hasn't arrived yet — once it does, every card below overrides
    // with the backend value so numbers reflect ALL orgs, not page-1 derived).
    const clientStats = {
      organizations_count: dashboardData.organizationsCount,
      goals_count: dashboardData.goalsCount,
      initiatives_count: dashboardData.initiativesCount,
      kpis_count: strategicGoalKpis.length,
      users_count: dashboardData.usersCount,
      commitment_index: commitmentIndex,
      vision_achievement: visionAchievement,
      target_percentage: targetPercentage,
      actual_percentage: actualPercentage,
      delayed_projects_percentage: delayedPercentage,
      completed_initiatives_percentage: completedPercentage,
      pending_goals_count: pendingGoalsCount,
      pending_initiatives_count: pendingInitiativesCount,
      excellent_kpis_count: excellentKpisCount,
      all_orgs_active: orgs.every((o: any) => o.is_active !== false),
      initiative_kpis_count: initiativeKpis.length,
      initiative_excellent_kpis: initiativeExcellentKpis,
      process_kpis_count: processKpis.length,
      process_excellent_kpis: processExcellentKpis,
      overall_achievement_rate: overallAchievementRate,
      achievement_improvement: 0,
      performance_deviation: performanceDeviation,
      risks_count: dashboardData.risksCount,
      units_count: dashboardData.unitsCount,
      services_count: dashboardData.servicesCount,
      processes_count: dashboardData.processesCount,
      sub_processes_count: dashboardData.subProcessesCount,
      employees_count: dashboardData.employeesCount,
      unit_goal_kpis_count: unitGoalKpisCount,
    };

    // Backend-aggregated totals override client-side fallbacks the moment
    // overviewData lands. Keys map 1:1 to the card values rendered below.
    if (overviewData) {
      return {
        ...clientStats,
        organizations_count: overviewData.organizations_count,
        all_orgs_active: overviewData.all_orgs_active,
        goals_count: overviewData.goals_count,
        pending_goals_count: overviewData.pending_goals_count,
        initiatives_count: overviewData.initiatives_count,
        pending_initiatives_count: overviewData.pending_initiatives_count,
        kpis_count: overviewData.strategic_kpis_count,
        excellent_kpis_count: overviewData.excellent_strategic_kpis,
        initiative_kpis_count: overviewData.initiative_kpis_count,
        initiative_excellent_kpis: overviewData.excellent_initiative_kpis,
        process_kpis_count: overviewData.process_kpis_count,
        process_excellent_kpis: overviewData.excellent_process_kpis,
        target_percentage: overviewData.target_percentage,
        actual_percentage: overviewData.actual_percentage,
        commitment_index: overviewData.commitment_index,
        vision_achievement: overviewData.actual_percentage,
        overall_achievement_rate: overviewData.overall_achievement_rate,
        performance_deviation: overviewData.performance_deviation,
        achievement_improvement: overviewData.achievement_improvement,
      };
    }

    return clientStats;
  }, [dashboardData, overviewData]);

  // Handle sort change for comparison chart — updates state, React Query
  // re-fetches because the new sort is a new queryKey.
  const handleComparisonSortChange = useCallback((field: string, order: 'asc' | 'desc') => {
    setComparisonSortBy(field);
    setComparisonOrder(order);
  }, []);

  const onRefresh = useCallback(() => {
    refetchDashboard();
    refetchOverview();
    refetchComparison();
  }, [refetchDashboard, refetchOverview, refetchComparison]);

  // Filtered + sorted chart data. Memoized because it feeds a chart that
  // re-renders on every ScrollView scroll; previously this re-ran its full
  // filter/sort/map pipeline on every render.
  const verticalBarChartData = useMemo<VerticalBarData[]>(() => {
    const filtered = selectedOrgId !== null
      ? organizations.filter(org => org.id === selectedOrgId)
      : organizations;
    return [...filtered]
      .sort((a, b) => (b.vision_achievement ?? 0) - (a.vision_achievement ?? 0))
      .map((org) => ({
        name: translateOrg(org.name),
        value: org.vision_achievement ?? 0,
      }));
  }, [organizations, selectedOrgId, translateOrg]);

  // Get selected organization name for dropdown display
  const selectedOrgName = selectedOrgId !== null
    ? translateOrg(organizations.find(org => org.id === selectedOrgId)?.name) || t('strategyDashboard.allOrganizations')
    : t('strategyDashboard.allOrganizations');

  // No global blocking spinner — each pillar renders its own skeleton via
  // `pillarPending`. The frame (Navbar, breadcrumb, header) renders instantly.

  return (
    <PageContainer>
      <Navbar />
      <Breadcrumb
        items={[
          { label: t('strategyDashboard.breadcrumbHome') },
          { label: t('strategyDashboard.breadcrumbStrategy') },
        ]}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          { padding: containerPadding }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        showsVerticalScrollIndicator={false}
      >
        {/* Header Section */}
        <View style={styles.headerSection}>
          <Text style={[styles.headerTitle, themedCardStyles.headerTitle, { fontSize: isMobile ? 20 : 24 }]}>{t('strategyDashboard.welcomeTitle')}</Text>
          {isDG(user) ? (
            <Text style={[styles.headerSubtitle, themedCardStyles.headerSubtitle]}>{t('strategyDashboard.welcomeSubtitle')}</Text>
          ) : (
            <Text style={[styles.headerSubtitle, themedCardStyles.headerSubtitle]}>{userRoleLabel}</Text>
          )}
        </View>

        {/* Row 1: Vision Achievement Metrics (matching Figma design) */}
        {pillarPending.orgs ? (
          <View style={[
            styles.mainStatsRow,
            isWideScreen && styles.mainStatsRowWide,
            { gap: isMobile ? 12 : 12 }
          ]}>
            <GaugeCardSkeleton />
            <GaugeCardSkeleton />
            <GaugeCardSkeleton />
          </View>
        ) : (
        <View style={[
          styles.mainStatsRow,
          isWideScreen && styles.mainStatsRowWide,
          { gap: isMobile ? 12 : 12 }
        ]}>
          {/* Left Card - مؤشر تحقيق الرؤية (Vision Achievement Index) */}
          <View
            style={[
              styles.kpiGaugeCard,
              themedCardStyles.card,
              isWideScreen && styles.visionIndexCardWide,
              { minWidth: isMobile ? 0 : undefined }
            ]}
          >
            <View style={styles.kpiGaugeCardBody}>
              <SegmentedGauge actual={stats?.actual_percentage ?? 0} target={stats?.target_percentage ?? 0} size={120} strokeWidth={12} color={commitmentColor(stats?.commitment_index ?? 0)} />
              <View style={styles.kpiGaugeTextSection}>
                <Text style={[styles.kpiGaugeTitle, themedCardStyles.cardTitle]}>{t('strategyDashboard.visionAchievementIndex')}</Text>
                <View style={styles.legendItems}>
                  <View style={styles.legendRow}>
                    <Text style={[styles.legendRowValue, themedCardStyles.cardValue]}>% {formatPct(stats?.target_percentage ?? 100)}</Text>
                    <View style={styles.legendRowContent}>
                      <Text style={[styles.legendRowLabel, themedCardStyles.cardDescription]}>{t('strategyDashboard.targetPercentage')}</Text>
                      <TargetEyeIcon size={20} color={colors.textSecondary} />
                    </View>
                  </View>
                  <View style={styles.legendRow}>
                    <Text style={[styles.legendRowValue, themedCardStyles.cardValue]}>% {formatPct(stats?.actual_percentage ?? 0)}</Text>
                    <View style={styles.legendRowContent}>
                      <Text style={[styles.legendRowLabel, themedCardStyles.cardDescription]}>{t('strategyDashboard.actualPercentage')}</Text>
                      <FlagIndicatorIcon size={20} color={colors.textSecondary} />
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* Right Card - مؤشر الالتزام بتحقيق الرؤية (Commitment Index) */}
          <View
            style={[
              styles.kpiGaugeCard,
              themedCardStyles.card,
              isWideScreen && styles.commitmentCardWide,
              { minWidth: isMobile ? 0 : undefined }
            ]}
          >
            <View style={styles.kpiGaugeCardBody}>
              <CircularGauge
                percentage={stats?.commitment_index ?? 0}
                size={120}
                strokeWidth={12}
                color={commitmentColor(stats?.commitment_index ?? 0)}
              />
              <View style={styles.kpiGaugeTextSection}>
                <Text style={[styles.kpiGaugeTitle, themedCardStyles.cardTitle, { fontFamily: FONTS.medium, paddingTop: 0 }]}>{t('strategyDashboard.commitmentIndex')}</Text>
                <Text style={[styles.kpiGaugeSubtitle, themedCardStyles.cardDescription]}>{t('strategyDashboard.commitmentIndexDescription')}</Text>
              </View>
            </View>
          </View>

          {/* Far-Left Card - مؤشر الإنحراف في تحقيق الرؤيا (Vision Deviation Index) */}
          <TouchableOpacity
            style={[
              styles.kpiGaugeCard,
              themedCardStyles.card,
              isWideScreen && styles.visionDeviationCardWide,
              { minWidth: isMobile ? 0 : undefined }
            ]}
            onPress={() => navigation.navigate('PerformanceDeviation')}
            activeOpacity={0.7}
          >
            <View style={styles.kpiGaugeCardBody}>
              <CircularGauge
                percentage={Math.max(0, Math.min(100, 100 - (stats?.commitment_index ?? 0)))}
                size={120}
                strokeWidth={12}
                color={deviationColor(Math.max(0, Math.min(100, 100 - (stats?.commitment_index ?? 0))))}
              />
              <View style={styles.kpiGaugeTextSection}>
                <Text style={[styles.kpiGaugeTitle, themedCardStyles.cardTitle, { fontFamily: FONTS.medium, paddingTop: 0 }]}>{t('strategyDashboard.visionDeviationIndex')}</Text>
                <Text style={[styles.kpiGaugeSubtitle, themedCardStyles.cardDescription]}>{t('strategyDashboard.visionDeviationIndexDescription')}</Text>
              </View>
            </View>
            <View style={styles.visionDeviationViewDetails}>
              <EyeIcon size={14} color={colors.textMuted} />
              <Text style={[styles.visionDeviationViewDetailsText, { color: colors.textMuted }]}>{t('strategyDashboard.viewDetails')}</Text>
            </View>
          </TouchableOpacity>
        </View>
        )}

        {/* Row 2 — Figma 6322:172163 (Risks / Initiatives / Goals / Gov) */}
        {(pillarPending.orgs || pillarPending.goals || pillarPending.initiatives || pillarPending.kpis || pillarPending.counts) ? (
          <StatCardSkeleton count={4} />
        ) : (
        <View style={[
          styles.figmaStatRow,
          { flexDirection: isMobile ? 'column' : 'row' }
        ]}>
          {/* RTL right → left: Risks, Initiatives, Goals, Gov */}
          <FigmaStatCard
            title={t('strategyDashboard.strategicRisks')}
            value={stats?.risks_count ?? 0}
            icon={<TriangleAlertIcon size={24} color={colors.textPrimary} />}
            themedCardStyle={themedCardStyles.card}
            valueColor={colors.text}
            titleColor={colors.textPrimary}
            onPress={() => navigation.navigate('RisksStack' as any, { screen: 'Risks' })}
          />
          <FigmaStatCard
            title={t('strategyDashboard.strategicInitiatives')}
            value={stats?.initiatives_count ?? 0}
            icon={<RocketStatIcon size={24} color={colors.textPrimary} />}
            chip={t('strategyDashboard.initiativeKpiChip', { count: stats?.initiative_kpis_count ?? 0 })}
            themedCardStyle={themedCardStyles.card}
            valueColor={colors.text}
            titleColor={colors.textPrimary}
            onPress={() => navigation.navigate('InitiativesStack' as any, { screen: 'Initiatives' })}
          />
          <FigmaStatCard
            title={t('strategyDashboard.strategicGoals')}
            value={stats?.goals_count ?? 0}
            icon={<GoalTargetIcon size={24} color={colors.textPrimary} />}
            chip={t('strategyDashboard.strategicKpiChip', { count: stats?.kpis_count ?? 0 })}
            themedCardStyle={themedCardStyles.card}
            valueColor={colors.text}
            titleColor={colors.textPrimary}
            onPress={() => navigation.navigate('GoalsStack' as any, { screen: 'Goals' })}
          />
          <FigmaStatCard
            title={t('strategyDashboard.governmentEntities')}
            value={stats?.organizations_count ?? 0}
            icon={<Building2Icon size={24} color={colors.textPrimary} />}
            chip={stats?.all_orgs_active ? t('strategyDashboard.allEntitiesActive') : null}
            themedCardStyle={themedCardStyles.card}
            valueColor={colors.text}
            titleColor={colors.textPrimary}
            onPress={() => navigation.navigate('OrganizationsList')}
          />
        </View>
        )}

        {/* Row 3 — Figma 6322:172168 (Human Capital / Units / Services / Processes) */}
        {pillarPending.counts ? (
          <StatCardSkeleton count={4} />
        ) : (
        <View style={[
          styles.figmaStatRow,
          { flexDirection: isMobile ? 'column' : 'row' }
        ]}>
          <FigmaStatCard
            title={t('strategyDashboard.humanCapital')}
            value={stats?.employees_count ?? 0}
            icon={<UsersStatIcon size={24} color={colors.textPrimary} />}
            themedCardStyle={themedCardStyles.card}
            valueColor={colors.text}
            titleColor={colors.textPrimary}
          />
          <FigmaStatCard
            title={t('strategyDashboard.organizationalUnits')}
            value={stats?.units_count ?? 0}
            icon={<BoxesIcon size={24} color={colors.textPrimary} />}
            chip={t('strategyDashboard.unitKpiChip', { count: stats?.unit_goal_kpis_count ?? 0 })}
            themedCardStyle={themedCardStyles.card}
            valueColor={colors.text}
            titleColor={colors.textPrimary}
          />
          <FigmaStatCard
            title={t('strategyDashboard.services')}
            value={stats?.services_count ?? 0}
            icon={<HandServicesIcon size={24} color={colors.textPrimary} />}
            themedCardStyle={themedCardStyles.card}
            valueColor={colors.text}
            titleColor={colors.textPrimary}
          />
          <FigmaStatCard
            title={t('strategyDashboard.mainProcesses')}
            value={stats?.processes_count ?? 0}
            icon={<FileCogIcon size={24} color={colors.textPrimary} />}
            chip={t('strategyDashboard.subProcessChip', { count: stats?.sub_processes_count ?? 0 })}
            themedCardStyle={themedCardStyles.card}
            valueColor={colors.text}
            titleColor={colors.textPrimary}
          />
        </View>
        )}

        {/* Pending Approvals Table Section - الاعتمادات في الانتظار - Hidden for executive (view-only) users */}
        {canSeePending && (
        <View style={[styles.tableSection, themedCardStyles.card]}>
          <View style={styles.tableSectionHeader}>
            <Text style={[styles.tableSectionTitle, themedCardStyles.sectionTitle]}>{t('strategyDashboard.pendingApprovals')}</Text>
          </View>

          {/* Horizontal scroll wrapper for mobile */}
          <ScrollView
            horizontal={isMobile}
            showsHorizontalScrollIndicator={isMobile}
            contentContainerStyle={isMobile ? { minWidth: 900 } : undefined}
          >
            <View style={{ width: isMobile ? 900 : '100%' }}>
              {/* Table Header - RTL order: first item = RIGHT side, last item = LEFT side */}
              <View style={[styles.tableHeader, themedCardStyles.tableHeader]}>
                <Text style={[styles.tableHeaderCell, styles.tableColOrgName, themedCardStyles.tableCell]}>{t('strategyDashboard.organizationColumn')}</Text>
                <Text style={[styles.tableHeaderCell, styles.tableColGoalsCount, themedCardStyles.tableCell]}>{t('strategyDashboard.strategicGoalsColumn')}</Text>
                {canSeePending && (
                  <Text style={[styles.tableHeaderCell, styles.tableColPendingGoals, themedCardStyles.tableCell]}>{t('strategyDashboard.goalsNeedApprovalColumn')}</Text>
                )}
                <Text style={[styles.tableHeaderCell, styles.tableColInitCount, themedCardStyles.tableCell]}>{t('strategyDashboard.initiativesColumn')}</Text>
                {canSeePending && (
                  <Text style={[styles.tableHeaderCell, styles.tableColPendingInit, themedCardStyles.tableCell]}>{t('strategyDashboard.initiativesNeedApprovalColumn')}</Text>
                )}
                <Text style={[styles.tableHeaderCell, styles.tableColKpis, themedCardStyles.tableCell]}>{t('strategyDashboard.kpisCountColumn')}</Text>
                <View style={styles.tableColAction} />
              </View>

              {/* Table Rows */}
              {organizations.length === 0 ? (
                <View style={styles.emptyTable}>
                  <Text style={[styles.emptyTableText, themedCardStyles.cardDescription]}>{t('strategyDashboard.noOrganizations')}</Text>
                </View>
              ) : (
                organizations.map((org, index) => (
                  <View
                    key={org.id}
                    style={[styles.tableRow, themedCardStyles.tableRow, index % 2 === 1 && { backgroundColor: isDark ? colors.gray100 : '#F9FAFB' }]}
                  >
                    {/* RTL order: first item = RIGHT side, last item = LEFT side */}
                    <Text style={[styles.tableCell, styles.tableColOrgName, themedCardStyles.tableCell]} numberOfLines={2}>
                      {translateOrg(org.name)}
                    </Text>
                    <Text style={[styles.tableCell, styles.tableColGoalsCount, themedCardStyles.tableCell]}>
                      {org.strategic_goals_count ?? 0}
                    </Text>
                    {canSeePending && (
                      <View style={[styles.tableColPendingGoals, styles.pendingBadgeCell]}>
                        <View style={styles.pendingBadge}>
                          <Text style={styles.pendingBadgeText}>
                            {org.pending_goals_count ?? 0}
                          </Text>
                        </View>
                      </View>
                    )}
                    <Text style={[styles.tableCell, styles.tableColInitCount, themedCardStyles.tableCell]}>
                      {org.initiatives_count ?? 0}
                    </Text>
                    {canSeePending && (
                      <View style={[styles.tableColPendingInit, styles.pendingBadgeCell]}>
                        <View style={styles.pendingBadge}>
                          <Text style={styles.pendingBadgeText}>
                            {org.pending_initiatives_count ?? 0}
                          </Text>
                        </View>
                      </View>
                    )}
                    <Text style={[styles.tableCell, styles.tableColKpis, themedCardStyles.tableCell]}>
                      {org.kpis_count ?? 0}
                    </Text>
                    <TouchableOpacity
                      style={[styles.tableColAction, styles.actionCell, { minHeight: 44 }]}
                      onPress={() => navigation.navigate('Goals', { organizationId: org.id })}
                    >
                      <EyeIcon size={18} color={colors.textMuted} />
                    </TouchableOpacity>
                  </View>
                ))
              )}
            </View>
          </ScrollView>

          {/* Pagination */}
          {organizations.length > 0 && (
            <View style={[styles.tablePagination, { flexDirection: isMobile ? 'column' : rtlRow, gap: isMobile ? 12 : 0 }]}>
              <View style={[styles.paginationLeft, { flexDirection: rtlRow }]}>
                <TouchableOpacity style={[styles.paginationButton, { minHeight: 44 }]}>
                  <Text style={[styles.paginationButtonText, themedCardStyles.cardDescription]}>{t('strategyDashboard.previous')}</Text>
                  <ChevronRightIcon size={16} color={colors.textMuted} />
                </TouchableOpacity>
                <View style={[styles.paginationPageNumber, { borderColor: colors.cardBorder }]}>
                  <Text style={[styles.paginationPageText, themedCardStyles.tableCell]}>1</Text>
                </View>
                <TouchableOpacity style={[styles.paginationButton, { minHeight: 44 }]}>
                  <ChevronLeftIcon size={16} color={colors.textMuted} />
                  <Text style={[styles.paginationButtonText, themedCardStyles.cardDescription]}>{t('strategyDashboard.next')}</Text>
                </TouchableOpacity>
              </View>
              <Text style={[styles.paginationInfo, themedCardStyles.cardDescription]}>
                {t('strategyDashboard.showingItems', { count: organizations.length, total: organizations.length })}
              </Text>
            </View>
          )}
        </View>
        )}

        {/* Organizations Performance Comparison Section - مقارنة أداء الجهات */}
        {canSeeComparison && (
          <OrganizationsComparisonSection
            data={comparisonData}
            loading={comparisonLoading}
            sortBy={comparisonSortBy}
            order={comparisonOrder}
            onSortChange={handleComparisonSortChange}
            goalsFromDate={goalsDateRange.fromDate}
            goalsToDate={goalsDateRange.toDate}
            goals={dashboardData?.goals as any}
          />
        )}

        {/* Organization Performance Cards Section - أداء الجهات */}
        <View style={styles.orgCardsSection}>
          <Text style={[styles.orgCardsSectionTitle, themedCardStyles.sectionTitle]}>{t('strategyDashboard.organizationPerformance')}</Text>
          {organizations.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={[styles.emptyStateText, themedCardStyles.cardDescription]}>{t('common.noData')}</Text>
            </View>
          ) : (
            <View style={[
              styles.orgCardsGrid,
              isWideScreen && styles.orgCardsGridWide,
              { gap: isMobile ? 12 : 20 }
            ]}>
              {organizations.map((org) => (
                <View
                  key={org.id}
                  style={[
                    styles.orgCard,
                    themedCardStyles.card,
                    isWideScreen && styles.orgCardWide,
                    { width: isMobile ? '100%' : isTablet ? '48%' : '31%', minWidth: isMobile ? 0 : 300 }
                  ]}
                >
                  {/* Organization Name */}
                  <Text style={[styles.orgCardName, themedCardStyles.cardTitle]} numberOfLines={2}>{translateOrg(org.name)}</Text>

                  {/* Two Donut Charts Row */}
                  <View style={styles.orgCardChartsRow}>
                    {/* مؤشر انحراف الأداء - Performance Deviation
                        Computed as 100 - commitment_index so the org-card
                        deviation always equals the rest of the commitment
                        gauge inside that org's detail page (visually:
                        deviation + commitment = 100). */}
                    {(() => {
                      const commitment = org.commitment_index;
                      const deviation = commitment != null
                        ? Math.max(0, Math.min(100, 100 - Number(commitment)))
                        : null;
                      return (
                        <View style={styles.orgCardChartItem}>
                          {deviation != null ? (
                            // When deviation is exactly 0 (commitment is
                            // 100%), draw the whole ring in translucent
                            // green so the card visibly signals "no
                            // deviation" instead of an empty gray ring.
                            deviation === 0 ? (
                              <ProgressCircle
                                percentage={100}
                                size={64}
                                strokeWidth={12}
                                color="rgba(34, 197, 94, 0.35)"
                                backgroundColor="rgba(34, 197, 94, 0.35)"
                                showLabel={false}
                              />
                            ) : (
                              <ProgressCircle
                                percentage={deviation}
                                size={64}
                                strokeWidth={12}
                                color={deviationColor(deviation)}
                                showLabel={false}
                              />
                            )
                          ) : (
                            <View style={{ width: 64, height: 64, justifyContent: 'center', alignItems: 'center' }}>
                              <Text style={{ fontSize: 20, fontWeight: '700', color: themedCardStyles.progressText }}>--</Text>
                            </View>
                          )}
                          <View style={styles.orgCardChartLabels}>
                            <Text style={[styles.orgCardChartLabel, themedCardStyles.cardDescription]}>{t('strategyDashboard.performanceDeviationIndex')}</Text>
                            <Text style={[styles.orgCardChartValue, themedCardStyles.cardValue]}>{deviation != null ? `%${formatPct(deviation)}` : '--'}</Text>
                          </View>
                        </View>
                      );
                    })()}

                    {/* مؤشر تحقيق الرؤية - Vision Achievement (target + actual on chart) */}
                    <View style={styles.orgCardChartItem}>
                      <SegmentedGauge
                        actual={Number(((org.stage_actual_progress ?? 0) * 100).toFixed(2))}
                        target={Number(((org.stage_required_progress ?? 0) * 100).toFixed(2))}
                        size={64}
                        strokeWidth={12}
                        showLabel={false}
                        showDot={false}
                        color={commitmentColor(org.commitment_index ?? 0)}
                      />
                      <View style={styles.orgCardChartLabels}>
                        <Text style={[styles.orgCardChartLabel, themedCardStyles.cardDescription]}>{t('strategyDashboard.visionAchievementIndex')}</Text>
                        <Text style={[styles.orgCardChartValue, themedCardStyles.cardValue]}>%{formatPct((org.stage_actual_progress ?? 0) * 100)}</Text>
                      </View>
                    </View>
                  </View>

                  {/* Progress Bars Section - Horizontal Bar Chart Design */}
                  <View style={styles.orgCardProgressSection}>
                    {/* الأهداف الاستراتيجية */}
                    <View style={styles.orgProgressItem}>
                      <View style={styles.orgProgressHeader}>
                        <Text style={[styles.orgProgressValue, themedCardStyles.cardValue]}>{formatPct(org.strategic_goals_achievement ?? 0)} %</Text>
                        <Text style={[styles.orgProgressLabel, themedCardStyles.cardTitle]}>{t('strategyDashboard.strategicGoals')}</Text>
                      </View>
                      <View style={[styles.orgProgressBarBg, { backgroundColor: isDark ? colors.gray200 : 'rgba(51, 65, 85, 0.1)' }]}>
                        <View style={[styles.orgProgressBarFill, { width: `${org.strategic_goals_achievement ?? 0}%` }]} />
                      </View>
                    </View>

                    {/* المبادرات الاستراتيجية */}
                    <View style={styles.orgProgressItem}>
                      <View style={styles.orgProgressHeader}>
                        <Text style={[styles.orgProgressValue, themedCardStyles.cardValue]}>{formatPct(org.initiatives_achievement ?? 0)} %</Text>
                        <Text style={[styles.orgProgressLabel, themedCardStyles.cardTitle]}>{t('strategyDashboard.strategicInitiatives')}</Text>
                      </View>
                      <View style={[styles.orgProgressBarBg, { backgroundColor: isDark ? colors.gray200 : 'rgba(51, 65, 85, 0.1)' }]}>
                        <View style={[styles.orgProgressBarFill, { width: `${org.initiatives_achievement ?? 0}%` }]} />
                      </View>
                    </View>

                    {/* العمليات (Processes) */}
                    <View style={styles.orgProgressItem}>
                      <View style={styles.orgProgressHeader}>
                        <Text style={[styles.orgProgressValue, themedCardStyles.cardValue]}>{formatPct(org.processes_achievement ?? 0)} %</Text>
                        <Text style={[styles.orgProgressLabel, themedCardStyles.cardTitle]}>{t('sidebar.processes')}</Text>
                      </View>
                      <View style={[styles.orgProgressBarBg, { backgroundColor: isDark ? colors.gray200 : 'rgba(51, 65, 85, 0.1)' }]}>
                        <View style={[styles.orgProgressBarFill, { width: `${org.processes_achievement ?? 0}%` }]} />
                      </View>
                    </View>

                    {/* الخدمات (Services) */}
                    <View style={styles.orgProgressItem}>
                      <View style={styles.orgProgressHeader}>
                        <Text style={[styles.orgProgressValue, themedCardStyles.cardValue]}>{formatPct(org.services_achievement ?? 0)} %</Text>
                        <Text style={[styles.orgProgressLabel, themedCardStyles.cardTitle]}>{t('sidebar.services')}</Text>
                      </View>
                      <View style={[styles.orgProgressBarBg, { backgroundColor: isDark ? colors.gray200 : 'rgba(51, 65, 85, 0.1)' }]}>
                        <View style={[styles.orgProgressBarFill, { width: `${org.services_achievement ?? 0}%` }]} />
                      </View>
                    </View>

                    {/* الوحدات التنظيمية */}
                    <View style={styles.orgProgressItem}>
                      <View style={styles.orgProgressHeader}>
                        <Text style={[styles.orgProgressValue, themedCardStyles.cardValue]}>{formatPct(org.units_achievement ?? 0)} %</Text>
                        <Text style={[styles.orgProgressLabel, themedCardStyles.cardTitle]}>{t('sidebar.organizationalUnits')}</Text>
                      </View>
                      <View style={[styles.orgProgressBarBg, { backgroundColor: isDark ? colors.gray200 : 'rgba(51, 65, 85, 0.1)' }]}>
                        <View style={[styles.orgProgressBarFill, { width: `${org.units_achievement ?? 0}%` }]} />
                      </View>
                    </View>

                    {/* رأس المال البشري */}
                    <View style={styles.orgProgressItem}>
                      <View style={styles.orgProgressHeader}>
                        <Text style={[styles.orgProgressValue, themedCardStyles.cardValue]}>{formatPct(org.human_capital_achievement ?? 0)} %</Text>
                        <Text style={[styles.orgProgressLabel, themedCardStyles.cardTitle]}>{t('strategyDashboard.humanCapital')}</Text>
                      </View>
                      <View style={[styles.orgProgressBarBg, { backgroundColor: isDark ? colors.gray200 : 'rgba(51, 65, 85, 0.1)' }]}>
                        <View style={[styles.orgProgressBarFill, { width: `${org.human_capital_achievement ?? 0}%` }]} />
                      </View>
                    </View>
                  </View>

                  {/* View Details Link — jumps into the DashboardScreen
                      (the org_employee-style org dashboard) scoped to the
                      clicked org via the organizationId route param. */}
                  <TouchableOpacity
                    style={styles.orgCardLink}
                    onPress={() => navigation.navigate('DashboardStack' as any, {
                      screen: 'Dashboard',
                      params: { organizationId: org.id },
                    })}
                  >
                    <ChevronLeftIcon size={16} color={colors.textSecondary} />
                    <Text style={[styles.orgCardLinkText, { color: colors.textSecondary }]}>{t('common.viewDetails')}</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          )}
        </View>
               {/* Due Readings & Initiative Status Row - RTL order: Initiative Status (RIGHT), Due Readings (LEFT) */}
        <View style={[
          styles.statsCardsRow,
          { flexDirection: isMobile ? 'column' : 'row-reverse', gap: isMobile ? 16 : 20 }
        ]}>
          {/* حالة المبادرات الاستراتيجية - Initiative Status (RIGHT in RTL) */}
          <View style={[styles.initiativeStatusCard, themedCardStyles.card, { minWidth: isMobile ? 0 : undefined }]}>
            <Text style={[styles.cardTitle, themedCardStyles.cardTitle]}>{t('strategyDashboard.initiativeStatus')}</Text>
            <View style={[
              styles.initiativeStatusContent,
              { flexDirection: isMobile ? 'column' : 'row-reverse', gap: isMobile ? 16 : 0 }
            ]}>
              {/* Donut Chart */}
              <InitiativeDonutChart data={initiativeStatus} />

              {/* Legend */}
              <View style={styles.initiativeLegend}>
                {/* تحت التخطيط - Planning */}
                <View style={styles.legendItem}>
                  <View style={styles.initiativeLegendRow}>
                    <Text style={[styles.legendValue, themedCardStyles.cardValue]}>
                      {initiativeStatus.total > 0
                        ? formatPct((initiativeStatus.planning / initiativeStatus.total) * 100)
                        : '0.00'} %
                    </Text>
                    <View style={styles.legendLabelRow}>
                      <Text style={[styles.legendLabel, themedCardStyles.cardDescription]}>{t('strategyDashboard.planning')}</Text>
                      <View style={[styles.legendDot, { backgroundColor: '#1F77B4' }]} />
                    </View>
                  </View>
                </View>
                {/* قيد التنفيذ - In Progress */}
                <View style={styles.legendItem}>
                  <View style={styles.initiativeLegendRow}>
                    <Text style={[styles.legendValue, themedCardStyles.cardValue]}>
                      {initiativeStatus.total > 0
                        ? formatPct((initiativeStatus.inProgress / initiativeStatus.total) * 100)
                        : '0.00'} %
                    </Text>
                    <View style={styles.legendLabelRow}>
                      <Text style={[styles.legendLabel, themedCardStyles.cardDescription]}>{t('strategyDashboard.inProgress')}</Text>
                      <View style={[styles.legendDot, { backgroundColor: '#C67EFF' }]} />
                    </View>
                  </View>
                </View>
                {/* مكتملة - Completed */}
                <View style={styles.legendItem}>
                  <View style={styles.initiativeLegendRow}>
                    <Text style={[styles.legendValue, themedCardStyles.cardValue]}>
                      {initiativeStatus.total > 0
                        ? formatPct((initiativeStatus.completed / initiativeStatus.total) * 100)
                        : '0.00'} %
                    </Text>
                    <View style={styles.legendLabelRow}>
                      <Text style={[styles.legendLabel, themedCardStyles.cardDescription]}>{t('strategyDashboard.completed')}</Text>
                      <View style={[styles.legendDot, { backgroundColor: '#2CA02C' }]} />
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>

          {/* جدول القراءات المستحقة - Due Readings Schedule (LEFT in RTL) */}
          <View style={[styles.dueReadingsCard, themedCardStyles.card, { minWidth: isMobile ? 0 : undefined }]}>
            <Text style={[styles.cardTitle, themedCardStyles.cardTitle]}>{t('strategyDashboard.dueReadingsSchedule')}</Text>
            <View style={[
              styles.dueReadingsGrid,
              { flexDirection: isMobile ? 'column' : 'row-reverse', gap: isMobile ? 12 : 16 }
            ]}>
              {/* اليوم - Today (RIGHT in RTL) */}
              <View style={[styles.dueReadingItem, { width: isMobile ? '100%' : 'auto', backgroundColor: isDark ? colors.gray100 : '#F8FAFC' }]}>
                <Text style={[styles.dueReadingLabel, themedCardStyles.cardDescription]}>{t('strategyDashboard.today')}</Text>
                <Text style={[styles.dueReadingValue, themedCardStyles.cardValue, { fontSize: isMobile ? 24 : 30 }]}>{dueReadings.today}</Text>
              </View>
              {/* هذا الاسبوع - This Week (CENTER) */}
              <View style={[styles.dueReadingItem, { width: isMobile ? '100%' : 'auto', backgroundColor: isDark ? colors.gray100 : '#F8FAFC' }]}>
                <Text style={[styles.dueReadingLabel, themedCardStyles.cardDescription]}>{t('strategyDashboard.thisWeek')}</Text>
                <Text style={[styles.dueReadingValue, themedCardStyles.cardValue, { fontSize: isMobile ? 24 : 30 }]}>{dueReadings.thisWeek}</Text>
              </View>
              {/* هذا الشهر - This Month (LEFT in RTL) */}
              <View style={[styles.dueReadingItem, { width: isMobile ? '100%' : 'auto', backgroundColor: isDark ? colors.gray100 : '#F8FAFC' }]}>
                <Text style={[styles.dueReadingLabel, themedCardStyles.cardDescription]}>{t('strategyDashboard.thisMonth')}</Text>
                <Text style={[styles.dueReadingValue, themedCardStyles.cardValue, { fontSize: isMobile ? 24 : 30 }]}>{dueReadings.thisMonth}</Text>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </PageContainer>
  );
}

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },

  // Header
  headerSection: {
    marginBottom: 24,
  },
  headerTitle: {
    fontSize: 24,
    fontFamily: FONTS.medium,
    color: '#111827',
    textAlign: 'right',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
  },
  headerWelcome: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
    marginTop: 8,
  },

  // Progress Circle Label
  progressLabel: {
    position: 'absolute',
    fontFamily: FONTS.medium,
  },

  // Main Stats Row
  mainStatsRow: {
    flexDirection: 'column',
    gap: 12,
    marginBottom: 16,
  },
  mainStatsRowWide: {
    flexDirection: 'row-reverse',
  },

  // Commitment Card (Large - Right side in RTL)
  commitmentCard: {
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
  },
  commitmentCardWide: {
    flex: 1,
  },
  visionDeviationCardWide: {
    flex: 1,
  },
  visionDeviationViewDetails: {
    position: 'absolute',
    bottom: 12,
    right: 20,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  visionDeviationViewDetailsText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
  },
  commitmentCardBody: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 20,
  },
  commitmentCardContent: {
    flex: 1,
  },
  commitmentCardTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#111827',
    textAlign: 'right',
    marginBottom: 8,
  },
  commitmentCardSubtitle: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
    lineHeight: 18,
  },

  // Vision Index Card (Smaller - Left side in RTL)
  visionIndexCard: {
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
  },
  visionIndexCardWide: {
    flex: 1,
  },
  visionIndexHeader: {
    marginBottom: 12,
  },
  visionIndexTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#111827',
    textAlign: 'right',
  },
  visionIndexBody: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  visionIndexStats: {
    flex: 1,
    gap: 12,
  },
  visionIndexDivider: {
    height: 1,
    backgroundColor: '#E5E7EB',
    marginVertical: 4,
  },
  visionIndexStatRow: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  visionIndexStatLabel: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 6,
  },
  visionIndexStatText: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  visionIndexStatValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#111827',
  },

  // KPI Gauge Card (matching DashboardScreen)
  kpiGaugeCard: {
    borderRadius: 8,
    padding: 20,
    height: 160,
    borderWidth: 1,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 1,
  },
  kpiGaugeCardBody: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: 20,
    flex: 1,
  },
  kpiGaugeTextSection: {
    flex: 1,
    minWidth: 180,
  },
  kpiGaugeTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#111827',
    textAlign: 'right',
    marginBottom: 16,
  },
  kpiGaugeSubtitle: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    fontWeight: '400',
    color: '#64748B',
    textAlign: 'right',
    marginTop: 0,
    lineHeight: 19.6,
  },
  kpiGaugeDescription: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
    lineHeight: 18,
  },
  legendItems: {
    width: '100%',
    gap: 16,
  },
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    gap: 8,
    width: '100%',
  },
  legendRowValue: {
    fontSize: 20,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#303030',
  },
  legendRowContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  legendRowLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  legendDotIndicator: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },

  // Main Count Stats Row (matching Figma design)
  mainCountStatsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  mainCountStatsRowWide: {
    flexWrap: 'nowrap',
    flexDirection: 'row-reverse', // RTL: rightmost card first
  },

  // ---- Figma Stat Card (6322:172163 / 6322:172168 — ArCard4) ----
  figmaStatRow: {
    gap: 20,
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  figmaStatCard: {
    flex: 1,
    minWidth: 180,
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 1,
    gap: 16,
  },
  figmaStatCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  figmaStatCardTitle: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    lineHeight: 19.6,
    textAlign: 'right',
    writingDirection: 'rtl',
  },
  figmaStatCardIcon: {
    width: 40,
    height: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  figmaStatCardBody: {
    gap: 4,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  figmaStatCardValue: {
    fontSize: 30,
    fontFamily: FONTS.numbers,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 39.6,
  },
  figmaStatCardChip: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  figmaStatCardChipText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#2CA02C',
    textAlign: 'right',
    writingDirection: 'rtl',
  },

  // Main Count Card (matching Figma)
  mainCountCard: {
    flex: 1,
    minWidth: 200,
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
  },
  mainCountCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginBottom: 12,
  },
  mainCountCardTitle: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: '#6B7280',
    textAlign: 'right',
  },
  mainCountCardValue: {
    fontSize: 32,
    fontFamily: FONTS.numbers,
    fontWeight: 'bold',
    color: '#111827',
    textAlign: 'center',
    marginBottom: 12,
  },
  mainCountCardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  mainCountCardSubtitle: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'center',
  },

  // Count Stats Row (legacy)
  countStatsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
  },
  countStatsRowWide: {
    flexWrap: 'nowrap',
  },

  // Count Card (legacy)
  countCard: {
    flex: 1,
    minWidth: 140,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 12,
  },
  countCardIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countCardContent: {
    flex: 1,
  },
  countCardValue: {
    fontSize: 24,
    fontFamily: FONTS.numbers,
    fontWeight: '500',
    color: '#111827',
    textAlign: 'right',
  },
  countCardLabel: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right',
  },

  // Table Section
  tableSection: {
    borderRadius: 12,
    borderWidth: 1,
    marginBottom: 24,
    overflow: 'hidden',
  },
  tableSectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tableSectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#111827',
    textAlign: 'right',
  },
  viewAllButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  viewAllText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#A9211F',
  },

  // Table - RTL layout for Arabic
  tableHeader: {
    flexDirection: 'row-reverse',
    backgroundColor: '#F9FAFB',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tableHeaderCell: {
    fontSize: 11,
    fontFamily: FONTS.medium,
    color: '#6B7280',
    textAlign: 'center',
  },
  tableRow: {
    flexDirection: 'row-reverse',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
    alignItems: 'center',
  },
  tableRowAlt: {
    backgroundColor: '#FAFAFA',
  },
  tableCell: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#374151',
    textAlign: 'center',
  },

  // Table Columns - Pending Approvals (matching Figma)
  tableColAction: { width: 40, alignItems: 'center' as const, justifyContent: 'center' as const },
  tableColKpis: { width: 90, textAlign: 'center' as const },
  tableColPendingInit: { width: 120, textAlign: 'center' as const },
  tableColInitCount: { width: 130, textAlign: 'center' as const },
  tableColPendingGoals: { width: 120, textAlign: 'center' as const },
  tableColGoalsCount: { width: 130, textAlign: 'center' as const },
  tableColOrgName: { flex: 1, minWidth: 150, textAlign: 'right' as const },

  // Pending badge styles (cyan/teal - for بحاجة اعتماد columns)
  pendingBadgeCell: {
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },
  pendingBadge: {
    backgroundColor: '#E0F7FA',
    paddingHorizontal: 16,
    paddingVertical: 4,
    borderRadius: 4,
    minWidth: 50,
    alignItems: 'center' as const,
  },
  pendingBadgeText: {
    color: '#00ACC1',
    fontFamily: FONTS.medium,
    fontSize: 12,
  },

  // Action Cell
  actionCell: {
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
  },

  // Pagination
  tablePagination: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  paginationLeft: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 8,
  },
  paginationButton: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    paddingVertical: 6,
    paddingHorizontal: 10,
    gap: 4,
  },
  paginationButtonText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },
  paginationPageNumber: {
    width: 32,
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    backgroundColor: '#FFFFFF',
  },
  paginationPageText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#374151',
  },
  paginationInfo: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#6B7280',
    textAlign: 'right' as const,
  },

  // Empty Table
  emptyTable: {
    padding: 40,
    alignItems: 'center',
  },
  emptyTableText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },

  // Due Readings & Initiative Status Row
  statsCardsRow: {
    flexDirection: 'row-reverse' as const,
    gap: 20,
    marginBottom: 24,
  },

  // Due Readings Card
  dueReadingsCard: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    padding: 20,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right' as const,
    marginBottom: 24,
  },
  dueReadingsGrid: {
    flexDirection: 'row-reverse' as const,
    gap: 16,
  },
  dueReadingItem: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
    alignItems: 'center' as const,
    gap: 16,
  },
  dueReadingLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'center' as const,
  },
  dueReadingValue: {
    fontSize: 30,
    fontFamily: FONTS.numbers,
    fontWeight: 'bold',
    color: '#414042',
    textAlign: 'center' as const,
  },

  // Initiative Status Card
  initiativeStatusCard: {
    flex: 1,
    borderRadius: 8,
    borderWidth: 1,
    padding: 20,
    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
    elevation: 2,
  },
  initiativeStatusContent: {
    flexDirection: 'row-reverse' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    flex: 1,
  },
  initiativeLegend: {
    gap: 16,
    justifyContent: 'center' as const,
  },
  legendItem: {
    alignItems: 'flex-end' as const,
  },
  initiativeLegendRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 8,
  },
  legendLabelRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 4,
  },
  legendDot: {
    width: 8,
    height: 32,
    borderRadius: 2,
  },
  legendLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right' as const,
  },
  legendValue: {
    fontSize: 20,
    fontFamily: FONTS.numbers,
    fontWeight: '600',
    color: '#303030',
  },

  // Chart Section
  chartSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    marginBottom: 24,
  },
  chartSectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#111827',
    textAlign: 'right',
    marginBottom: 16,
  },

  // Filters Card - high z-index to appear above chart
  filtersCard: {
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 1000,
    flexDirection: 'row-reverse' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'flex-start' as const,
    flexWrap: 'wrap' as const,
    gap: 16,
    zIndex: 1000,
    position: 'relative' as const,
  },
  chartFilterItem: {
    flex: 1,
    minWidth: 140,
    alignItems: 'flex-end' as const,
    gap: 6,
    position: 'relative' as const,
    zIndex: 1001,
  },
  chartFilterLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right' as const,
  },
  chartFilterDropdown: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    gap: 8,
    minWidth: 150,
    width: '100%' as const,
  },
  chartFilterDate: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    justifyContent: 'space-between' as const,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    gap: 8,
    width: '100%' as const,
  },
  chartFilterValue: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#374151',
    flex: 1,
    textAlign: 'right' as const,
  },

  // Dropdown Menu - highest z-index
  dropdownMenu: {
    position: 'absolute' as const,
    top: '100%' as const,
    left: 0,
    right: 0,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    marginTop: 4,
    maxHeight: 200,
    zIndex: 1002,
    elevation: 1002,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
  },
  dropdownItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  dropdownItemSelected: {
    backgroundColor: '#F3F4F6',
  },
  dropdownItemText: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#374151',
    textAlign: 'right' as const,
  },
  dropdownItemTextSelected: {
    fontFamily: FONTS.medium,
    color: '#A9211F',
  },
  dropdownBackdrop: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1001,
    elevation: 1001,
  },
  dropdownScroll: {
    maxHeight: 200,
  },

  // Vertical Bar Chart - lower z-index than filters
  verticalChartContainer: {
    flexDirection: 'row' as const,
    alignItems: 'stretch' as const,
    zIndex: 1,
    elevation: 1,
    width: '100%' as const,
  },
  yAxis: {
    width: 45,
    height: 240,
    justifyContent: 'space-between' as const,
    alignItems: 'flex-end' as const,
    paddingRight: 8,
    marginTop: 0,
  },
  yAxisLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  chartScrollView: {
    flex: 1,
  },
  // Wrapper to ensure full width
  chartAreaWrapper: {
    flex: 1,
    width: '100%' as const,
  },
  // Chart area - always full width.
  // paddingBottom leaves room for several wrapped label lines so the
  // long Arabic department names display fully.
  chartAreaFullWidth: {
    height: 400,
    paddingBottom: 150,
    position: 'relative' as const,
    width: '100%' as const,
    minWidth: '100%' as const,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  chartArea: {
    height: 320,
    paddingBottom: 80,
    position: 'relative' as const,
    minWidth: 800,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
  },
  gridLines: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    height: 240,
    justifyContent: 'space-between' as const,
  },
  gridLine: {
    height: 0,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
    borderStyle: 'dashed' as const,
  },
  // Bars section container (bars + labels)
  barsSection: {
    paddingHorizontal: 28,
  },
  // Centered bars section (when few items)
  barsSectionCentered: {
    paddingHorizontal: 0,
  },
  // Bars row - fixed height, all bars start from bottom baseline
  barsRow: {
    flexDirection: 'row-reverse' as const,
    alignItems: 'flex-end' as const,
    justifyContent: 'flex-start' as const,
    height: 240,
  },
  // Centered bars row (when few items)
  barsRowCentered: {
    justifyContent: 'center' as const,
  },
  // Each bar column - same width, centered
  verticalBarColumn: {
    width: 70,
    marginHorizontal: 8,
    alignItems: 'center' as const,
    justifyContent: 'flex-end' as const,
  },
  // The actual bar - grows UP from baseline
  verticalBar: {
    width: 48,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  // Labels row - separate from bars, below baseline
  labelsRow: {
    flexDirection: 'row-reverse' as const,
    justifyContent: 'flex-start' as const,
    marginTop: 10,
  },
  // Centered labels row (when few items)
  labelsRowCentered: {
    justifyContent: 'center' as const,
  },
  // Each label column - matches bar column width.
  // Note: NO alignItems:center here. We let the Text fill the column
  // width (textAlign:center on the Text handles horizontal centering)
  // so it can wrap onto multiple lines instead of sizing to its
  // intrinsic single-line width.
  labelColumn: {
    width: 70,
    marginHorizontal: 8,
  },
  // Label text — fills the parent column and wraps long Arabic dept
  // names onto multiple lines. width:'100%' forces the Text to take
  // the full column width so it wraps instead of laying out as a
  // single inline line.
  verticalBarLabel: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'center' as const,
    width: '100%' as const,
    lineHeight: 15,
    // Web: force wrap inside the column even for long unbreakable
    // strings, and override any inherited nowrap/ellipsis.
    whiteSpace: 'normal' as any,
    wordBreak: 'break-word' as any,
    overflowWrap: 'break-word' as any,
  },

  // Organization Cards Section - White card container with shadow
  orgCardsSection: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 20,
    marginBottom: 24,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
  },
  orgCardsSectionTitle: {
    fontSize: 16,
    fontFamily: FONTS.bold,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 20,
  },
  orgCardsGrid: {
    flexDirection: 'row-reverse' as const,
    flexWrap: 'wrap' as const,
    gap: 20,
    justifyContent: 'flex-start' as const,
  },
  orgCardsGridWide: {
    justifyContent: 'flex-start' as const,
  },
  emptyState: {
    padding: 40,
    alignItems: 'center' as const,
    justifyContent: 'center' as const,
    borderRadius: 8,
    borderWidth: 1,
  },
  emptyStateText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#6B7280',
  },

  // Organization Card (Figma exact)
  orgCard: {
    width: '31%' as const,
    minWidth: 300,
    borderRadius: 8,
    borderWidth: 1,
    padding: 12,
    gap: 16,
  },
  orgCardWide: {
    width: '31%' as const,
    minWidth: 300,
  },
  orgCardName: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right' as const,
    lineHeight: 24,
  },

  // Charts Row
  orgCardChartsRow: {
    flexDirection: 'row' as const,
    justifyContent: 'center' as const,
    gap: 8,
  },
  orgCardChartItem: {
    flex: 1,
    alignItems: 'center' as const,
    gap: 8,
  },
  orgCardChartLabels: {
    alignItems: 'center' as const,
    gap: 2,
  },
  orgCardChartLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'center' as const,
  },
  orgCardChartValue: {
    fontSize: 12,
    fontFamily: FONTS.numbers,
    fontWeight: '500' as const,
    color: '#303030',
    textAlign: 'center' as const,
  },
  orgCardChartValuesRow: {
    alignItems: 'center' as const,
    gap: 2,
  },

  // Progress Section
  orgCardProgressSection: {
    gap: 16,
  },
  orgProgressItem: {
    gap: 8,
  },
  orgProgressHeader: {
    flexDirection: 'row' as const,
    justifyContent: 'space-between' as const,
    alignItems: 'center' as const,
  },
  orgProgressLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right' as const,
    flex: 1,
  },
  orgProgressValue: {
    fontSize: 14,
    fontFamily: FONTS.numbers,
    fontWeight: '500' as const,
    color: '#414042',
  },
  orgProgressBarBg: {
    height: 8,
    backgroundColor: 'rgba(51, 65, 85, 0.1)',
    borderRadius: 8,
    overflow: 'hidden' as const,
    width: '100%' as const,
    flexDirection: 'row-reverse' as const,
  },
  orgProgressBarFill: {
    height: '100%' as const,
    backgroundColor: '#A9211F',
    borderRadius: 8,
  },

  // View Details Link
  orgCardLink: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 8,
    paddingVertical: 3,
    paddingHorizontal: 8,
  },
  orgCardLinkText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right' as const,
  },
});
